/**
 * Office Availability Service
 * Centralized service for checking office availability based on timezone and business hours
 *
 * @class OfficeAvailabilityService
 */
class OfficeAvailabilityService {
  /**
   * Get the browser's local timezone
   * @returns {string} - IANA timezone identifier
   */
  static getBrowserTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      console.warn('OfficeAvailabilityService: Could not detect browser timezone', error);
      return 'America/New_York'; // Fallback to EST
    }
  }

  /**
   * Get weekday and wall-clock time for an instant in a specific timezone.
   * Intl avoids parsing a localized date string back in the browser timezone.
   * @param {Date} date - Instant to convert
   * @param {string} timezone - IANA timezone
   * @returns {{day: number, hour: number, minutes: number}}
   */
  static getZonedTimeParts(date, timezone) {
    const weekdayNumbers = {
      Sun: 0,
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
    };
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(date);
    const getPart = (type) => parts.find((part) => part.type === type)?.value;

    return {
      day: weekdayNumbers[getPart('weekday')],
      hour: Number(getPart('hour')),
      minutes: Number(getPart('minute')),
    };
  }

  /**
   * Check if an office is currently available
   * @param {Object} config - Office configuration
   * @param {string} config.timezone - IANA timezone (e.g., "America/New_York")
   * @param {string} config.openTime - Opening time in 24h format (e.g., "08:00" or "8")
   * @param {string} config.closeTime - Closing time in 24h format (e.g., "17:00" or "17")
   * @param {Array<number>} config.operatingDays - Array of day numbers (0=Sunday, 1=Monday, etc.)
   * @param {Date} now - Instant to check (defaults to now)
   * @returns {boolean} - True if office is currently available
   */
  static checkAvailability(
    { timezone, openTime, closeTime, operatingDays = [1, 2, 3, 4, 5] },
    now = new Date()
  ) {
    try {
      // Validate inputs
      if (!timezone || !openTime || !closeTime) {
        console.warn('OfficeAvailabilityService: Missing required parameters', {
          timezone,
          openTime,
          closeTime,
        });
        return false;
      }

      const { day, hour, minutes } = this.getZonedTimeParts(now, timezone);
      const currentTimeInMinutes = hour * 60 + minutes;

      // Check if it's an operating day
      if (!operatingDays.includes(day)) {
        return false;
      }

      // Parse open and close times
      const openMinutes = this.parseTime(openTime);
      const closeMinutes = this.parseTime(closeTime);

      // Check if current time is within business hours
      return currentTimeInMinutes >= openMinutes && currentTimeInMinutes < closeMinutes;
    } catch (error) {
      console.error('OfficeAvailabilityService: Error checking availability', error);
      return false;
    }
  }

  /**
   * Parse time string to minutes since midnight
   * Supports formats: "HH:MM", "H:MM", "HH", "H"
   * @param {string} timeString - Time in 24h format
   * @returns {number} - Minutes since midnight
   */
  static parseTime(timeString) {
    if (!timeString) return 0;

    // Handle "HH:MM" or "H:MM" format
    if (timeString.includes(':')) {
      const [hours, minutes] = timeString.split(':').map((part) => parseInt(part.trim(), 10));
      return hours * 60 + (minutes || 0);
    }

    // Handle "HH" or "H" format (just hours)
    const hours = parseInt(timeString.trim(), 10);
    return hours * 60;
  }

  /**
   * Format time in minutes to readable 12-hour format
   * @param {number} minutes - Minutes since midnight
   * @returns {string} - Formatted time (e.g., "8:00 AM")
   */
  static formatTime(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) return `12:${mins.toString().padStart(2, '0')} AM`;
    if (hours < 12) return `${hours}:${mins.toString().padStart(2, '0')} AM`;
    if (hours === 12) return `12:${mins.toString().padStart(2, '0')} PM`;
    return `${hours - 12}:${mins.toString().padStart(2, '0')} PM`;
  }

  /**
   * Convert office time to browser's local timezone
   * @param {string} officeTimezone - Office's IANA timezone
   * @param {string} timeString - Time in 24h format (e.g., "08:00")
   * @returns {Object} - { hours, minutes, totalMinutes } in browser's timezone
   */
  static convertOfficeTimeToLocal(officeTimezone, timeString) {
    try {
      const browserTimezone = this.getBrowserTimezone();
      
      // If timezones are the same, no conversion needed
      if (officeTimezone === browserTimezone) {
        const minutes = this.parseTime(timeString);
        return {
          hours: Math.floor(minutes / 60),
          minutes: minutes % 60,
          totalMinutes: minutes
        };
      }
      
      // Parse the office time
      const officeMinutes = this.parseTime(timeString);
      const officeHours = Math.floor(officeMinutes / 60);
      const officeMins = officeMinutes % 60;
      
      // Get current date in office timezone
      const now = new Date();
      const officeDate = new Date(now.toLocaleString('en-US', { timeZone: officeTimezone }));
      
      // Create date representing the office opening time in office timezone
      // Format as ISO string that toLocaleString will parse
      const year = officeDate.getFullYear();
      const month = officeDate.getMonth();
      const day = officeDate.getDate();
      
      // Create a reference time: midnight in office timezone
      const midnightInOffice = new Date(now);
      midnightInOffice.setHours(0, 0, 0, 0);
      
      // Calculate what time it is in office timezone right now
      const nowInOffice = new Date(now.toLocaleString('en-US', { timeZone: officeTimezone }));
      const nowInBrowser = new Date(now.toLocaleString('en-US', { timeZone: browserTimezone }));
      
      // Calculate offset in minutes between office and browser timezone
      const offsetMs = nowInBrowser.getTime() - nowInOffice.getTime();
      const offsetMinutes = Math.round(offsetMs / (1000 * 60));
      
      // Apply offset to office time to get browser time
      const browserMinutes = officeMinutes + offsetMinutes;
      
      // Handle day wrap-around (convert negative or > 1440 to 0-1439 range)
      const normalizedMinutes = ((browserMinutes % 1440) + 1440) % 1440;
      
      return {
        hours: Math.floor(normalizedMinutes / 60),
        minutes: normalizedMinutes % 60,
        totalMinutes: normalizedMinutes
      };
    } catch (error) {
      console.error('OfficeAvailabilityService: Error converting timezone', error);
      // Fallback: return original time
      const minutes = this.parseTime(timeString);
      return {
        hours: Math.floor(minutes / 60),
        minutes: minutes % 60,
        totalMinutes: minutes
      };
    }
  }

  /**
   * Get next available time message
   * @param {Object} config - Office configuration
   * @param {string} config.timezone - IANA timezone
   * @param {string} config.openTime - Opening time in 24h format
   * @param {string} config.closeTime - Closing time in 24h format
   * @param {Array<number>} config.operatingDays - Array of day numbers
   * @param {string} config.timezoneDisplay - Display name for timezone (e.g., "EST")
   * @returns {string} - Next available time message
   */
  static getNextAvailableTime({
    timezone,
    openTime,
    closeTime,
    operatingDays = [1, 2, 3, 4, 5],
    timezoneDisplay = '',
  }) {
    try {
      const browserTimezone = this.getBrowserTimezone();
      
      const now = new Date();
      const { day, hour, minutes } = this.getZonedTimeParts(now, timezone);
      const currentTimeInMinutes = hour * 60 + minutes;

      const openMinutes = this.parseTime(openTime);
      const closeMinutes = this.parseTime(closeTime);

      // Convert office hours to browser's local timezone for display
      const localOpenTime = this.convertOfficeTimeToLocal(timezone, openTime);
      const localCloseTime = this.convertOfficeTimeToLocal(timezone, closeTime);
      
      const openingTimeFormatted = this.formatTime(localOpenTime.totalMinutes);
      const closingTimeFormatted = this.formatTime(localCloseTime.totalMinutes);
      
      // Use browser timezone abbreviation for display
      const displayTimezone = this.getTimezoneAbbreviation(browserTimezone);
      const tzSuffix = displayTimezone ? ` ${displayTimezone}` : '';

      // If it's an operating day but before opening time
      if (operatingDays.includes(day) && currentTimeInMinutes < openMinutes) {
        return `We open today at ${openingTimeFormatted}${tzSuffix}`;
      }

      // If it's an operating day but after closing time
      if (operatingDays.includes(day) && currentTimeInMinutes >= closeMinutes) {
        const nextDay = this.getNextBusinessDay(day, operatingDays);
        return `We'll be back ${nextDay} at ${openingTimeFormatted}${tzSuffix}`;
      }

      // If it's not an operating day
      if (!operatingDays.includes(day)) {
        const nextDay = this.getNextBusinessDay(day, operatingDays);
        return `We'll be back ${nextDay} at ${openingTimeFormatted}${tzSuffix}`;
      }

      return `Hours: ${openingTimeFormatted}-${closingTimeFormatted}${tzSuffix}`;
    } catch (error) {
      console.error('OfficeAvailabilityService: Error getting next available time', error);
      return 'Hours vary';
    }
  }

  /**
   * Get next business day name
   * @param {number} currentDay - Current day (0=Sunday, 1=Monday, etc.)
   * @param {Array<number>} operatingDays - Array of operating day numbers
   * @returns {string} - Next business day ("tomorrow" or day name)
   */
  static getNextBusinessDay(currentDay, operatingDays) {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    // Find the next operating day
    for (let i = 1; i <= 7; i++) {
      const nextDay = (currentDay + i) % 7;
      if (operatingDays.includes(nextDay)) {
        if (i === 1) return 'tomorrow';
        return dayNames[nextDay];
      }
    }
    return 'soon';
  }

  /**
   * Parse operating days from various formats
   * @param {string|Array} daysInput - Operating days ("Mon-Fri", "1,2,3,4,5", or array)
   * @returns {Array<number>} - Array of day numbers (0=Sunday, 1=Monday, etc.)
   */
  static parseOperatingDays(daysInput) {
    // If already an array, return it
    if (Array.isArray(daysInput)) {
      return daysInput;
    }

    // If string, try to parse it
    if (typeof daysInput === 'string') {
      // Handle comma-separated numbers: "1,2,3,4,5"
      if (daysInput.match(/^[\d,\s]+$/)) {
        return daysInput.split(',').map((d) => parseInt(d.trim(), 10));
      }

      // Handle day abbreviations: "Mon-Fri", "Monday-Friday"
      const lowerDays = daysInput.toLowerCase();
      if (lowerDays.includes('mon') && lowerDays.includes('fri')) {
        return [1, 2, 3, 4, 5]; // Mon-Fri
      }
    }

    // Default to Mon-Fri
    return [1, 2, 3, 4, 5];
  }

  /**
   * Get timezone display abbreviation from IANA timezone
   * @param {string} timezone - IANA timezone (e.g., "America/New_York")
   * @returns {string} - Timezone abbreviation (e.g., "EST" or "EDT")
   */
  static getTimezoneAbbreviation(timezone) {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short',
      });
      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
      return timeZonePart ? timeZonePart.value : '';
    } catch (error) {
      console.error('OfficeAvailabilityService: Error getting timezone abbreviation', error);
      return '';
    }
  }
}

/**
 * Office Availability UI Controller
 * Manages the UI for displaying office availability status
 *
 * @class OfficeAvailabilityUI
 */
class OfficeAvailabilityUI {
  constructor(element) {
    this.element = element;
    this.dot = element.querySelector('[data-office-availability-dot]');
    this.text = element.querySelector('[data-office-availability-text]');

    // Read configuration from data attributes
    this.config = {
      timezone: element.dataset.timezone,
      openTime: element.dataset.openTime,
      closeTime: element.dataset.closeTime,
      operatingDays: element.dataset.operatingDays
        ? OfficeAvailabilityService.parseOperatingDays(element.dataset.operatingDays)
        : [1, 2, 3, 4, 5],
      timezoneDisplay: element.dataset.timezoneDisplay || '',
    };

    // Auto-detect timezone abbreviation if not provided
    if (!this.config.timezoneDisplay && this.config.timezone) {
      this.config.timezoneDisplay = OfficeAvailabilityService.getTimezoneAbbreviation(
        this.config.timezone
      );
    }

    // Initialize Bootstrap tooltip
    this.initTooltip();

    // Update status immediately
    this.updateStatus();

    // Update every minute
    this.intervalId = setInterval(() => this.updateStatus(), 60000);
  }

  initTooltip() {
    // Initialize Bootstrap tooltip (requires Bootstrap JS to be loaded)
    if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
      this.tooltip = new bootstrap.Tooltip(this.element);
    }
  }

  updateStatus() {
    const isAvailable = OfficeAvailabilityService.checkAvailability(this.config);

    if (isAvailable) {
      this.setAvailable();
    } else {
      this.setAway();
    }
  }

  setAvailable() {
    this.dot.className = 'rr-office-availability__dot rr-office-availability__dot--available';
    this.text.className = 'rr-office-availability__text rr-office-availability__text--available';
    this.text.textContent = 'Available';
    this.updateTooltip("We're currently available to help you! (excluding holidays)");
  }

  setAway() {
    this.dot.className = 'rr-office-availability__dot rr-office-availability__dot--away';
    this.text.className = 'rr-office-availability__text rr-office-availability__text--away';
    this.text.textContent = 'Away';

    const nextAvailable = OfficeAvailabilityService.getNextAvailableTime(this.config);
    this.updateTooltip(`We're away. ${nextAvailable} (excluding holidays)`);
  }

  updateTooltip(message) {
    // Update tooltip content
    this.element.setAttribute('title', message);
    this.element.setAttribute('data-bs-original-title', message);

    // Update tooltip instance if it exists
    if (this.tooltip) {
      this.tooltip.dispose();
      this.tooltip = new bootstrap.Tooltip(this.element);
    }
  }

  destroy() {
    // Cleanup
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
    if (this.tooltip) {
      this.tooltip.dispose();
    }
  }
}

/**
 * Initialize all office availability indicators on the page
 */
function initOfficeAvailabilityIndicators() {
  const indicators = document.querySelectorAll('[data-office-availability-indicator]');
  indicators.forEach((element) => {
    // Prevent double initialization
    if (!element._officeAvailability) {
      element._officeAvailability = new OfficeAvailabilityUI(element);
    }
  });
}

// Initialize when DOM is ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initOfficeAvailabilityIndicators);
  } else {
    initOfficeAvailabilityIndicators();
  }
}

// Export for use in other modules if needed
if (typeof window !== 'undefined') {
  window.OfficeAvailabilityService = OfficeAvailabilityService;
  window.OfficeAvailabilityUI = OfficeAvailabilityUI;
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = OfficeAvailabilityService;
}
